import type { ModoPeriodo, Periodo } from '@adelante/shared';
import { obtenerRangoPeriodo } from '@adelante/shared';
import { useState } from 'react';

/**
 * Estado de período seleccionado por el usuario.
 *
 * Soporta 4 modos: 'mes', 'trimestre', 'anio', 'rango'. Todos se persisten
 * en sessionStorage para que sobreviva navegación entre pantallas.
 *
 * El backend solo recibe el rango ya normalizado vía `obtenerRangoPeriodo`,
 * así que el `modo` es solo cosa de la UI.
 */

const KEY = 'adelante-periodo';

function periodoInicial(): Periodo {
  const hoy = new Date();
  return {
    modo: 'anio',
    anio: hoy.getFullYear(),
    mes: hoy.getMonth() + 1,
  };
}

function leer(): Periodo {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Periodo>;
      // Validación mínima: anio y mes son obligatorios para cualquier modo
      if (p.modo && p.anio && p.mes) {
        return {
          modo: p.modo,
          anio: p.anio,
          mes: p.mes,
          trimestre: p.trimestre,
          desdeAnio: p.desdeAnio,
          desdeMes: p.desdeMes,
          hastaAnio: p.hastaAnio,
          hastaMes: p.hastaMes,
        };
      }
    }
  } catch {}
  return periodoInicial();
}

function guardar(p: Periodo) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(p));
  } catch {}
}

export function usePeriodo() {
  const [periodo, setPeriodoState] = useState<Periodo>(leer);

  function setPeriodo(next: Periodo) {
    setPeriodoState(next);
    guardar(next);
  }

  /** Helper para cambiar el modo conservando anio/mes actuales como ancla. */
  function setModo(modo: ModoPeriodo) {
    const base: Periodo = { ...periodo, modo };
    if (modo === 'trimestre') {
      // Si no había trimestre, derivar del mes actual
      base.trimestre = base.trimestre ?? (Math.ceil(periodo.mes / 3) as 1 | 2 | 3 | 4);
    }
    if (modo === 'rango') {
      // Si no había rango, inicializar como [mes, mes]
      base.desdeAnio = base.desdeAnio ?? periodo.anio;
      base.desdeMes = base.desdeMes ?? periodo.mes;
      base.hastaAnio = base.hastaAnio ?? periodo.anio;
      base.hastaMes = base.hastaMes ?? periodo.mes;
    }
    setPeriodo(base);
  }

  /** Mutadores específicos por modo (la UI los usa según el modo activo). */
  function setAnio(anio: number) {
    setPeriodo({ ...periodo, anio });
  }
  function setMes(mes: number) {
    setPeriodo({ ...periodo, mes });
  }
  function setTrimestre(q: 1 | 2 | 3 | 4) {
    setPeriodo({ ...periodo, trimestre: q });
  }
  function setDesde(anio: number, mes: number) {
    setPeriodo({ ...periodo, desdeAnio: anio, desdeMes: mes });
  }
  function setHasta(anio: number, mes: number) {
    setPeriodo({ ...periodo, hastaAnio: anio, hastaMes: mes });
  }

  /**
   * Devuelve los query params que el backend espera, listos para mandar.
   * Convierte cualquier modo al formato `desdeAnio/desdeMes/hastaAnio/hastaMes`.
   */
  function parametrosAPI() {
    const r = obtenerRangoPeriodo(periodo);
    return {
      desdeAnio: r.desdeAnio,
      desdeMes: r.desdeMes,
      hastaAnio: r.hastaAnio,
      hastaMes: r.hastaMes,
    };
  }

  return {
    periodo,
    setPeriodo,
    setModo,
    setAnio,
    setMes,
    setTrimestre,
    setDesde,
    setHasta,
    parametrosAPI,
    // Atajos para compatibilidad con código viejo que solo leía anio/mes
    anio: periodo.anio,
    mes: periodo.mes,
  };
}
